import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Grid,
  Paper,
  Chip,
  CircularProgress,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Button,
} from "@mui/material";
import {
  FiSearch,
  FiFilter,
  FiTrendingUp,
  FiTrendingDown,
  FiBarChart2,
} from "react-icons/fi";
import { debounce } from "lodash";
import { API_ENDPOINT } from "../../../utils/constants";
import { getToken } from "../../../utils/util";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import useApiEp from "../../../hooks/useApiEp";

const ContactsAnalysisDashboard = () => {
  const { data } = useApiEp(`/database/database-analytics`);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    priority: "",
    minJobNo: "",
    maxJobNo: "",
    state: "",
    city: "",
    page: 1,
    limit: 50,
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 50,
  });

  const [analytics, setAnalytics] = useState({
    priorityDistribution: {},
    jobNoRange: { min: 0, max: 0 },
    stateDistribution: {},
  });

  // Priority options
  const priorityOptions = [
    { value: "1", label: "High Priority", color: "error" },
    { value: "2", label: "Medium Priority", color: "warning" },
    { value: "3", label: "Low Priority", color: "success" },
    { value: "4", label: "Critical", color: "error" },
  ];

  // Debounced search and filter updates
  const debouncedFetch = useCallback(
    debounce((newFilters) => {
      setFilters((prev) => ({ ...prev, ...newFilters, page: 1 }));
    }, 500),
    []
  );

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    debouncedFetch({ search: value });
  };

  const handleFilterChange = (field, value) => {
    // Validate numeric inputs
    if (
      ["minJobNo", "maxJobNo"].includes(field) &&
      value &&
      Number(value) < 0
    ) {
      return;
    }
    debouncedFetch({ [field]: value });
  };

  const handlePageChange = (event, value) => {
    setFilters((prev) => ({ ...prev, page: value }));
  };

  // Fetch contacts data
  const fetchContacts = async () => {
    setLoading(true);
    setError("");

    try {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          queryParams.append(key, value);
        }
      });

      const response = await fetch(
        `${API_ENDPOINT}/database/contacts-by-priority?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch contacts");
      }

      const data = await response.json();
      setContacts(data || []);
      setPagination(
        data.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: 50,
        }
      );

      // Calculate analytics
      calculateAnalytics(data.items || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Calculate analytics from contacts data
  const calculateAnalytics = (contactsData) => {
    if (!contactsData.length) {
      setAnalytics({
        priorityDistribution: {},
        jobNoRange: { min: 0, max: 0 },
        stateDistribution: {},
      });
      return;
    }

    const priorityCount = {};
    const stateCount = {};
    const jobNos = contactsData
      .map((contact) => contact.jobNo || 0)
      .filter(Boolean);

    contactsData.forEach((contact) => {
      const priority = contact.priority || "Unknown";
      priorityCount[priority] = (priorityCount[priority] || 0) + 1;

      const state = contact.state || "Unknown";
      stateCount[state] = (stateCount[state] || 0) + 1;
    });

    setAnalytics({
      priorityDistribution: priorityCount,
      jobNoRange: {
        min: Math.min(...jobNos) || 0,
        max: Math.max(...jobNos) || 0,
      },
      stateDistribution: stateCount,
    });
  };

  useEffect(() => {
    fetchContacts();
  }, [filters]);

  // Memoized pie chart data
  const pieChartData = useMemo(() => {
    return Object.entries(analytics.priorityDistribution).map(
      ([key, value]) => ({
        name: key,
        value,
      })
    );
  }, [analytics.priorityDistribution]);

  const COLORS = ["#FF6B6B", "#FFD93D", "#6BCB77", "#D55D92", "#4D96FF"];

  // Analytics Cards
  const AnalyticsCard = ({ title, value, icon: Icon, subtitle }) => (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="overline">
              {title}
            </Typography>
            <Typography variant="h4" component="div">
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Icon size={24} />
        </Box>
      </CardContent>
    </Card>
  );

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      "Name",
      "Email",
      "Phone",
      "Priority",
      "Job No",
      "Job Id",
      "Address",
      "City",
      "State",
      "Pin Code",
    ];
    const csvContent = [
      headers.join(","),
      ...contacts.map((contact) =>
        [
          contact.name,
          contact.email,
          contact.phone,
          contact.priority,
          contact.jobNo,
          contact.jobId,
          `"${contact.address.replace(/"/g, '""')}"`,
          contact.city,
          contact.state,
          contact.pinCode,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "contacts.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Contacts Analysis Dashboard
      </Typography>

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Search Contacts"
              variant="outlined"
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <FiSearch style={{ marginRight: 8, color: "#666" }} />
                ),
              }}
              placeholder="Search by name, email, or other fields..."
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={filters.priority}
                label="Priority"
                onChange={(e) => handleFilterChange("priority", e.target.value)}
              >
                <MenuItem value="">All Priorities</MenuItem>
                {priorityOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={6} md={1}>
            <TextField
              fullWidth
              label="Min Job No"
              type="number"
              value={filters.minJobNo}
              onChange={(e) => handleFilterChange("minJobNo", e.target.value)}
            />
          </Grid>

          <Grid item xs={6} md={1}>
            <TextField
              fullWidth
              label="Max Job No"
              type="number"
              value={filters.maxJobNo}
              onChange={(e) => handleFilterChange("maxJobNo", e.target.value)}
            />
          </Grid>

          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              label="State"
              value={filters.state}
              onChange={(e) => handleFilterChange("state", e.target.value)}
            />
          </Grid>

          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              label="City"
              value={filters.city}
              onChange={(e) => handleFilterChange("city", e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Analytics Overview */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <AnalyticsCard
            title="Total Contacts"
            value={pagination.totalItems}
            icon={FiBarChart2}
            subtitle={`Page ${pagination.currentPage} of ${pagination.totalPages}`}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <AnalyticsCard
            title="Priority Distribution"
            value={Object.keys(analytics.priorityDistribution).length}
            icon={FiTrendingUp}
            subtitle={Object.entries(analytics.priorityDistribution)
              .map(([key, count]) => `${key}: ${count}`)
              .join(", ")}
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <AnalyticsCard
            title="Job No Range"
            value={`${analytics.jobNoRange.min} - ${analytics.jobNoRange.max}`}
            icon={FiTrendingUp}
            subtitle="Current view range"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <AnalyticsCard
            title="State Distribution"
            value={Object.keys(analytics.stateDistribution).length}
            icon={FiTrendingDown}
            subtitle={Object.entries(analytics.stateDistribution)
              .map(([key, count]) => `${key}: ${count}`)
              .join(", ")}
          />
        </Grid>
      </Grid>

      {/* Priority Distribution Chart */}
      {pieChartData.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Priority Distribution Chart
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieChartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) =>
                  `${name} ${(percent * 100).toFixed(0)}%`
                }
              >
                {pieChartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Results */}
      <Paper sx={{ p: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6">
            Contacts ({contacts.length} of {pagination.totalItems})
          </Typography>
          <Button variant="contained" onClick={exportToCSV}>
            Export to CSV
          </Button>
          {loading && <CircularProgress size={24} />}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {contacts.length === 0 && !loading ? (
          <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
            No contacts found. Try adjusting your search criteria.
          </Typography>
        ) : (
          <>
            <Grid container spacing={2}>
              {contacts.map((contact, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {contact.name || "Unnamed Contact"}
                      </Typography>
                      <Chip
                        label={contact.priority || "No Priority"}
                        size="small"
                        color={
                          priorityOptions.find(
                            (opt) => opt.value === contact.priority
                          )?.color || "default"
                        }
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="body2" color="textSecondary">
                        Job No: {contact.jobNo || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Job ID: {contact.jobId || "N/A"}
                      </Typography>
                      {contact.email && (
                        <Typography variant="body2" color="textSecondary">
                          Email: {contact.email}
                        </Typography>
                      )}
                      <Typography variant="body2" color="textSecondary">
                        Phone: {contact.phone || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Address: {contact.address || "N/A"}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        City: {contact.city || "N/A"}, State:{" "}
                        {contact.state || "N/A"}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ContactsAnalysisDashboard;
